import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { authService } from './services/authService';
import apiService from './services/apiService';
import logger from './utils/logger';
import {
  normalizeAuthMeState,
  resolveAuthMeFailureAction,
  resolveRefreshPreconditionFailure,
} from './utils/authSessionState';
import {
  clearAuthenticatedClientState,
  clearCrossHouseholdClientState,
  getRememberedHouseholdIdForUser,
  rememberHouseholdIdForUser,
} from './utils/authClientCleanup';
import {
  buildLoginAuthContextState,
  buildNormalizedAuthContextState,
  normalizeAuthenticatedRequestEndpoint,
} from './utils/authContextState';
import { EMPTY_BILLING_STATUS } from './utils/billingStatusState';
import { hasFreshTrendsV2Capability } from './utils/trendsV2Capability';
import { purgeAllTrendsV2, purgeTrendsV2ExceptSession } from './services/trendsV2CacheBridge';
import { beginDesktopSnapshotMutation, clearDesktopSnapshotsExceptSession, isDesktopSnapshotMutation } from './services/desktopTransactionSnapshots';

const AuthContext = createContext(null);
const SIGNED_OUT_KEY = 'vylo:explicit-sign-out';
const hasSignedOutMarker = () => {
  try { return localStorage.getItem(SIGNED_OUT_KEY) !== null; } catch { return false; }
};
const persistSignedOut = (signedOut) => {
  try {
    if (signedOut) localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()));
    else localStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // The in-memory boundary still applies when browser storage is unavailable.
  }
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
let authSessionSequence = 0;
const createAuthSessionScope = (userId) => `${String(userId || 'user')}:${Date.now()}:${authSessionSequence += 1}`;
const retainOrCreateAuthSessionScope = (current, userId) => current && String(current).startsWith(`${String(userId || 'user')}:`)
  ? current
  : createAuthSessionScope(userId);

const refreshAfterConcurrentRotation = async (initialError, isCurrentSession) => {
  const delays = [initialError?.retryAfterMs ?? 100, 250, 500];
  let latestError = initialError;

  for (const delay of delays) {
    await wait(delay);
    if (!isCurrentSession()) return null;
    try {
      return await authService.refreshToken(undefined);
    } catch (error) {
      if (error?.code !== 'REFRESH_ALREADY_ROTATED') throw error;
      latestError = error;
    }
  }

  throw latestError;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [household, setHousehold] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [billingStatus, setBillingStatus] = useState(EMPTY_BILLING_STATUS);
  const [features, setFeatures] = useState({});
  const [featuresFresh, setFeaturesFresh] = useState(false);
  const [sessionScope, setSessionScope] = useState(null);
  const [authEpoch, setAuthEpoch] = useState(0);
  const sessionBoundaryRef = useRef({ epoch: 0, signedOut: hasSignedOutMarker() });
  const mountedRef = useRef(true);
  const refreshAttemptRef = useRef(0);
  const refreshInFlightRef = useRef(null);
  const billingStatusRef = useRef(billingStatus);
  const userRef = useRef(user);
  const householdRef = useRef(household);
  const authInfoRequestRef = useRef({ generation: 0, controller: null });

  const advanceSessionBoundary = useCallback((signedOut) => {
    const epoch = sessionBoundaryRef.current.epoch + 1;
    sessionBoundaryRef.current = { epoch, signedOut };
    refreshInFlightRef.current = null;
    refreshAttemptRef.current = 0;
    setAuthEpoch(epoch);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const invalidateAuthInfoRequests = useCallback(() => {
    const previous = authInfoRequestRef.current;
    previous.controller?.abort();
    authInfoRequestRef.current = { generation: previous.generation + 1, controller: null };
  }, []);

  const beginAuthInfoRequest = useCallback(() => {
    const previous = authInfoRequestRef.current;
    previous.controller?.abort();
    const request = {
      generation: previous.generation + 1,
      controller: new AbortController(),
    };
    authInfoRequestRef.current = request;
    return request;
  }, []);

  const isCurrentAuthInfoRequest = useCallback((request) => (
    authInfoRequestRef.current.generation === request.generation
      && authInfoRequestRef.current.controller === request.controller
      && !request.controller.signal.aborted
  ), []);

  useEffect(() => {
    billingStatusRef.current = billingStatus;
  }, [billingStatus]);

  useEffect(() => {
    userRef.current = user;
    householdRef.current = household;
  }, [household, user]);

  useEffect(() => {
    if (sessionScope) purgeTrendsV2ExceptSession(sessionScope);
    if (sessionScope) clearDesktopSnapshotsExceptSession(sessionScope);
  }, [sessionScope]);

  const trendsV2Enabled = hasFreshTrendsV2Capability({ features, fresh: featuresFresh });

  useEffect(() => {
    if (featuresFresh && sessionScope && !trendsV2Enabled) purgeAllTrendsV2();
  }, [featuresFresh, sessionScope, trendsV2Enabled]);

  const logout = useCallback(async (options = {}) => {
    advanceSessionBoundary(true);
    const logoutEpoch = sessionBoundaryRef.current.epoch;
    if (!options.fromAnotherTab) persistSignedOut(true);
    invalidateAuthInfoRequests();
    const logoutRefreshToken = refreshToken;
    const logoutUserId = userRef.current?.id;
    const logoutHouseholdId = householdRef.current?.id;
    userRef.current = null;
    householdRef.current = null;
    clearAuthenticatedClientState({
      userId: logoutUserId,
      householdId: logoutHouseholdId,
    });

    setUser(null);
    setHousehold(null);
    setAccessToken(null);
    setRefreshToken(null);
    setOnboardingComplete(false);
    setBillingStatus(EMPTY_BILLING_STATUS);
    setFeatures({});
    setFeaturesFresh(false);
    setSessionScope(null);
    setLoading(false);

    if (!options.localOnly) {
      try {
        await authService.logout(logoutRefreshToken);
      } catch (error) {
        logger.error('Logout error:', error);
      }
    }
    if (options.redirect === 'session-expired' && sessionBoundaryRef.current.epoch === logoutEpoch) {
      window.location.assign('/session-expired');
    }
  }, [advanceSessionBoundary, invalidateAuthInfoRequests, refreshToken]);

  useEffect(() => {
    const handleStorage = (event) => {
      // A background tab can receive this after a newer login removed the marker.
      if (event.key === SIGNED_OUT_KEY && event.newValue !== null) {
        void logout({ localOnly: true, fromAnotherTab: true });
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [logout]);

  const handleRefreshToken = useCallback(async ({ silent, revoked } = {}) => {
    const isCurrentSession = () => mountedRef.current
      && sessionBoundaryRef.current.epoch === authEpoch
      && !sessionBoundaryRef.current.signedOut
      && !hasSignedOutMarker();
    if (!isCurrentSession()) return null;
    if (revoked) {
      await logout({ localOnly: true });
      return null;
    }
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current.promise;
    }
    const csrfToken = authService.getCsrfToken();
    const refreshPreconditionFailure = resolveRefreshPreconditionFailure({
      silent,
      refreshToken,
      csrfToken,
    });
    if (refreshPreconditionFailure.shouldAbort) {
      if (refreshPreconditionFailure.shouldLogout) {
        logout({ redirect: refreshPreconditionFailure.redirect });
      }
      return null;
    }
    if (refreshPreconditionFailure.shouldLogout) {
      logout({ redirect: refreshPreconditionFailure.redirect });
      return null;
    }

    const attempt = {};
    refreshInFlightRef.current = attempt;
    const refreshPromise = (async () => {
      try {
        let data;
        try {
          data = await authService.refreshToken(refreshToken || undefined);
        } catch (error) {
          if (error?.code !== 'REFRESH_ALREADY_ROTATED') {
            throw error;
          }
          // A different tab rotated the shared HttpOnly cookie while this
          // request was waiting on the server-side token lock. The winning
          // response can reach the browser slightly after this 409, so use a
          // short bounded retry window for the newly committed cookie.
          data = await refreshAfterConcurrentRotation(error, isCurrentSession);
        }
        if (!isCurrentSession()) return null;
        if (!data?.accessToken || typeof data.accessToken !== 'string') {
          throw new Error('Invalid token refresh response');
        }
        invalidateAuthInfoRequests();
        setAccessToken(data.accessToken);
        if (typeof data.refreshToken === 'string' && data.refreshToken) {
          setRefreshToken(data.refreshToken);
        } else if (refreshToken) {
          setRefreshToken(null);
        }
        return data.accessToken;
      } catch (error) {
        if (!isCurrentSession()) return null;
        if (error.message === 'ACCESS_REVOKED') {
          logger.errorEvent('access_revoked', { reason: 'refresh' });
          await logout({ localOnly: true, redirect: 'session-expired' });
          return null;
        }
        logger.error('Error refreshing token:', error);
        if (!silent) {
          logout({ redirect: 'session-expired' });
        }
        return null;
      } finally {
        if (refreshInFlightRef.current === attempt) refreshInFlightRef.current = null;
      }
    })();

    attempt.promise = refreshPromise;
    return refreshPromise;
  }, [authEpoch, invalidateAuthInfoRequests, refreshToken, logout]);

  const fetchUserInfo = useCallback(async () => {
    if (!accessToken) {
      invalidateAuthInfoRequests();
      setFeaturesFresh(false);
      setLoading(false);
      return;
    }

    let activeRequest = beginAuthInfoRequest();
    let requestToken = accessToken;
    setFeaturesFresh(false);
    try {
      while (requestToken) {
        try {
          const data = await authService.getUserInfo(requestToken, { signal: activeRequest.controller.signal });
          if (!isCurrentAuthInfoRequest(activeRequest)) return;
          const normalizedAuthState = normalizeAuthMeState(data);
          const nextAuthState = buildNormalizedAuthContextState({
            normalizedAuthState,
            previousBillingStatus: billingStatusRef.current,
          });
          setUser(nextAuthState.user);
          setHousehold(nextAuthState.household);
          setOnboardingComplete(nextAuthState.onboardingComplete);
          setOnboardingStep(nextAuthState.onboardingStep);
          setBillingStatus(nextAuthState.billingStatus || EMPTY_BILLING_STATUS);
          setFeatures(nextAuthState.features || {});
          setFeaturesFresh(true);
          setSessionScope((current) => retainOrCreateAuthSessionScope(current, nextAuthState.user?.id));
          refreshAttemptRef.current = 0;
          return;
        } catch (error) {
          if (error?.name === 'AbortError' || !isCurrentAuthInfoRequest(activeRequest)) return;
          const failureAction = resolveAuthMeFailureAction({
            errorMessage: error.message,
            refreshAttemptCount: refreshAttemptRef.current,
          });
          if (failureAction.type === 'logout') {
            if (error.message === 'ACCESS_REVOKED') {
              logger.errorEvent('access_revoked', { reason: 'me' });
            }
            await logout({
              redirect: failureAction.redirect,
              localOnly: error.message === 'ACCESS_REVOKED',
            });
            return;
          }
          refreshAttemptRef.current += 1;
          const newToken = await handleRefreshToken();
          if (!newToken) return;
          requestToken = newToken;
          activeRequest = beginAuthInfoRequest();
        }
      }
    } finally {
      if (isCurrentAuthInfoRequest(activeRequest)) {
        authInfoRequestRef.current = { generation: activeRequest.generation, controller: null };
        setLoading(false);
      }
    }
  }, [accessToken, beginAuthInfoRequest, handleRefreshToken, invalidateAuthInfoRequests, isCurrentAuthInfoRequest, logout]);

  // Set up refresh token callback for API service
  useEffect(() => {
    apiService.setRefreshTokenCallback(handleRefreshToken);
    return () => apiService.setRefreshTokenCallback(null);
  }, [handleRefreshToken, logout]);

  // Check if user is authenticated on mount
  useEffect(() => {
    let cancelled = false;

    if (accessToken) {
      void fetchUserInfo().catch((error) => {
        if (error?.name !== 'AbortError') logger.error('Authentication bootstrap error:', error);
      });
      return () => {
        cancelled = true;
        invalidateAuthInfoRequests();
      };
    }

    (async () => {
      const token = await handleRefreshToken({ silent: true });
      if (!token && !cancelled) {
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      invalidateAuthInfoRequests();
    };
  }, [accessToken, fetchUserInfo, handleRefreshToken, invalidateAuthInfoRequests]);

  useEffect(() => {
    if (!household?.id || !user?.id) return;
    const currentHouseholdId = String(household.id);
    const lastHouseholdId = getRememberedHouseholdIdForUser(user.id);
    clearCrossHouseholdClientState({
      userId: user?.id,
      previousHouseholdId: lastHouseholdId,
      nextHouseholdId: currentHouseholdId,
    });
    rememberHouseholdIdForUser({
      userId: user.id,
      householdId: currentHouseholdId,
    });
  }, [household?.id, user?.id]);

  const login = useCallback((
    tokens,
    userData,
    householdData,
    onboardingData = null,
    nextBillingStatus = null,
    features = {}
  ) => {
    advanceSessionBoundary(false);
    persistSignedOut(false);
    invalidateAuthInfoRequests();
    const nextLoginState = buildLoginAuthContextState({
      tokens,
      userData,
      householdData,
      onboardingData,
      billingStatus: nextBillingStatus,
      features,
    });
    setAccessToken(nextLoginState.accessToken);
    userRef.current = nextLoginState.user;
    householdRef.current = nextLoginState.household;
    setRefreshToken(nextLoginState.refreshToken);
    setUser(nextLoginState.user);
    setHousehold(nextLoginState.household);
    setOnboardingComplete(nextLoginState.onboardingComplete);
    setOnboardingStep(nextLoginState.onboardingStep);
    setBillingStatus(nextLoginState.billingStatus || EMPTY_BILLING_STATUS);
    setFeatures(nextLoginState.features || {});
    setFeaturesFresh(false);
    setSessionScope(createAuthSessionScope(nextLoginState.user?.id));
  }, [advanceSessionBoundary, invalidateAuthInfoRequests]);

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(true);
    setOnboardingStep(2);
  }, []);

  const replaceAccessToken = useCallback((nextAccessToken) => {
    if (sessionBoundaryRef.current.epoch !== authEpoch || sessionBoundaryRef.current.signedOut || !mountedRef.current) return;
    if (typeof nextAccessToken === 'string' && nextAccessToken) {
      invalidateAuthInfoRequests();
      setAccessToken(nextAccessToken);
    }
  }, [authEpoch, invalidateAuthInfoRequests]);

  // Authenticated fetch wrapper that handles token refresh
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    const endpoint = normalizeAuthenticatedRequestEndpoint({
      url,
      baseUrl: apiService.baseUrl,
    });
    const finishSnapshotMutation = isDesktopSnapshotMutation(endpoint, options.method)
      ? beginDesktopSnapshotMutation(householdRef.current?.id) : null;
    try {
      return await apiService.authenticatedRequest(endpoint, options, accessToken);
    } finally {
      finishSnapshotMutation?.();
    }
  }, [accessToken]);

  const value = {
    user,
    household,
    accessToken,
    refreshToken,
    loading,
    isAuthenticated: !!user,
    onboardingComplete,
    onboardingStep,
    billingStatus,
    features,
    featuresFresh,
    sessionScope,
    trendsV2Enabled,
    login,
    logout,
    completeOnboarding,
    authenticatedFetch,
    refreshUserInfo: fetchUserInfo,
    replaceAccessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
